'use client';

import { QueryBoundary } from '@shared/api-utils';
import { Button, Skeleton } from '@shared/components/shadcn';
import { twMerge } from '@shared/themes';
import { useTranslations } from 'next-intl';
import type { ComponentProps, ReactNode } from 'react';
import { SecondaryHeaderContainer } from '../../SecondaryHeaderContainer/SecondaryHeaderContainer';
import { ScratchcardsCardGrid } from '../ScratchcardsCardGrid/ScratchcardsCardGrid';
import { SCRATCHCARD_API_CATEGORY, type ScratchcardCategory } from '../constants/scratchcardCategories';
import { useScratchcardsByCategory } from '../hooks/useScratchcardsByCategory/useScratchcardsByCategory';

const CATEGORY_PAGE_NAMESPACE = 'scratchcards.category_page';

type ScratchcardsCategoryPageContentProps = ComponentProps<'div'> & {
  category: ScratchcardCategory;
  skeletonItems?: number;
};

type CategoryPageLayoutProps = ComponentProps<'div'> & {
  category: ScratchcardCategory;
  headerContent?: ReactNode;
  children: ReactNode;
};

function CategoryPageLayout({ category, headerContent, className, children, ...props }: CategoryPageLayoutProps) {
  return (
    <div className={twMerge('ll-grid-container [--ll-container-max-width:880px]', className)} {...props}>
      <SecondaryHeaderContainer namespace="scratchcards.categories" title={category} className="ll-container-full">
        {headerContent ? <div className="ll-container-inner paragraph-s-strong text-foreground-subtle ml-10 mt-3">{headerContent}</div> : null}
      </SecondaryHeaderContainer>
      <div className="ll-container-inner pb-10">{children}</div>
    </div>
  );
}

function ScratchcardsCategoryPageContent({ category, skeletonItems = 6, className, ...props }: ScratchcardsCategoryPageContentProps) {
  const t = useTranslations(CATEGORY_PAGE_NAMESPACE);
  const query = useScratchcardsByCategory({
    category: SCRATCHCARD_API_CATEGORY[category],
  });

  const layoutProps = { category, className, ...props };

  return (
    <QueryBoundary
      query={query}
      loadingFallback={
        <CategoryPageLayout {...layoutProps} headerContent={<Skeleton className="w-16 h-4.5" />}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: skeletonItems }, (_, index) => (
              <Skeleton key={index} className="w-full h-72.5" />
            ))}
          </div>
        </CategoryPageLayout>
      }
      errorFallbackRender={({ resetErrorBoundary }) => (
        <CategoryPageLayout {...layoutProps}>
          <p className="col-span-full">{t('error')}</p>
          <Button variant="secondary" size="s" onClick={resetErrorBoundary} data-testid="scratchcards-category-retry">
            {t('retry')}
          </Button>
        </CategoryPageLayout>
      )}
    >
      {({ data: scratchcards }) => (
        <CategoryPageLayout {...layoutProps} headerContent={t('results', { number: scratchcards.length })}>
          {scratchcards.length === 0 ? <p className="col-span-full">{t('empty')}</p> : <ScratchcardsCardGrid scratchcards={scratchcards} namespace={CATEGORY_PAGE_NAMESPACE} />}
        </CategoryPageLayout>
      )}
    </QueryBoundary>
  );
}

export { ScratchcardsCategoryPageContent, type ScratchcardsCategoryPageContentProps };
export type { ScratchcardCategory };
